import type React from "react";
import { fmtHours, fmtInt } from "@/lib/format";
import type { Slide, SlideContext } from "../slide.types";
import { Card, CardLabel } from "../visuals/Card";
import { ACCENT_BLUE, ACCENT_ORANGE, TIER_COLORS, TIER_LABELS, type TierKey } from "../colors";

const TIER_ORDER: TierKey[] = ["high", "watch_list", "on_track", "excellent"];

function tierDot(tier: string | null): React.JSX.Element {
  const color =
    tier && tier in TIER_COLORS
      ? TIER_COLORS[tier as TierKey]
      : "rgba(255,255,255,0.2)";
  return (
    <span
      className="inline-block w-2 h-2 rounded-full flex-shrink-0"
      style={{ background: color }}
    />
  );
}

function TeamPerformanceSlideView({ data }: SlideContext): React.JSX.Element {
  const stores = data.methodology.stores;

  if (stores.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-white/40 text-sm font-body">
          No team data available for this period.
        </p>
      </div>
    );
  }

  const top = [...stores]
    .sort((a, b) => b.productive_hrs_per_device - a.productive_hrs_per_device)
    .slice(0, 20);

  const maxPhd = top[0]?.productive_hrs_per_device || 1;
  const gridTemplate = "2rem 1fr 7rem 6rem 6rem 1.5rem";

  return (
    <Card delay={100} className="overflow-hidden">
      <div className="flex items-center justify-between mb-5">
        <CardLabel>Top {top.length} Teams — Productive Hrs / Device</CardLabel>
        <span className="text-white/30 text-[11px] font-body uppercase tracking-widest">
          {stores.length} total teams
        </span>
      </div>

      <div
        className="grid gap-x-4 mb-3 px-1"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <span className="text-white/25 text-[10px] font-body uppercase tracking-widest">#</span>
        <span className="text-white/25 text-[10px] font-body uppercase tracking-widest">Team</span>
        <span className="text-white/25 text-[10px] font-body uppercase tracking-widest text-right">
          Prod Hrs/Device
        </span>
        <span className="text-white/25 text-[10px] font-body uppercase tracking-widest text-right">
          Prod Hours
        </span>
        <span className="text-white/25 text-[10px] font-body uppercase tracking-widest text-right">
          Sessions
        </span>
        <span />
      </div>

      <div className="space-y-2">
        {top.map((team, i) => {
          const barWidth = (team.productive_hrs_per_device / maxPhd) * 100;
          const isTop3 = i < 3;
          return (
            <div
              key={`${team.store}-${i}`}
              className="group relative"
              style={{ animation: `pmFade 500ms ease-out ${100 + i * 40}ms both` }}
            >
              <div
                className="grid gap-x-4 items-center px-1 py-2 rounded-xl transition-colors group-hover:bg-white/5"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                <span
                  className="font-head font-bold text-sm"
                  style={{ color: isTop3 ? ACCENT_ORANGE : "rgba(255,255,255,0.25)" }}
                >
                  {i + 1}
                </span>

                <div className="min-w-0">
                  <p className="text-white/90 text-sm font-body truncate mb-1.5">{team.store}</p>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${barWidth}%`,
                        background: isTop3 ? ACCENT_ORANGE : ACCENT_BLUE,
                        animation: `pmBarGrow 800ms ease-out ${150 + i * 40}ms both`,
                        transformOrigin: "left",
                      }}
                    />
                  </div>
                </div>

                <span
                  className="font-head font-semibold text-sm text-right"
                  style={{ color: isTop3 ? ACCENT_ORANGE : "rgba(255,255,255,0.85)" }}
                >
                  {team.productive_hrs_per_device.toFixed(1)}h
                </span>

                <span className="text-white/60 text-sm font-body text-right">
                  {fmtHours(team.productive_hours)}
                </span>

                <span className="text-white/60 text-sm font-body text-right">
                  {fmtInt(team.sessions)}
                </span>

                <div className="flex justify-center">{tierDot(team.tier)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-4 mt-5 pt-4 border-t border-white/5">
        {TIER_ORDER.map((k) => (
          <div key={k} className="flex items-center gap-1.5">
            <span
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ background: TIER_COLORS[k] }}
            />
            <span className="text-white/30 text-[10px] font-body uppercase tracking-wider">
              {TIER_LABELS[k]}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export const teamPerformanceSlide: Slide = {
  id: "team-performance",
  title: "Team Performance",
  render: (ctx) => <TeamPerformanceSlideView {...ctx} />,
};
